import { appHttpService } from '@/services/standard/http-service';
import { httpToClientError, HttpStatus, models } from 'cesg-shared';

class AdminProjectsService {
  async getAll(): Promise<models.ListAdminProjectsResponse> {
    const adminProjects = await appHttpService.request
      .url(`/admin-projects/`)
      .get()
      .json<models.ListAdminProjectsResponse>();
    return adminProjects;
  }

  async getArchived(): Promise<models.ListAdminProjectsResponse> {
    const adminProjects = await appHttpService.request
      .url(`/admin-projects/archived`)
      .get()
      .json<models.ListAdminProjectsResponse>();
    return adminProjects;
  }

  async getProject(projectId: string): Promise<models.AdminProjectResponse> {
    const adminProjects = await appHttpService.request
      .url(`/admin-projects/project/${projectId}`)
      .get()
      .json<models.AdminProjectResponse>();
    return adminProjects;
  }

  async createProject(
    fortnoxId: string,
    name: string,
  ): Promise<models.ListAdminProjectsResponse> {
    const createdProject = await appHttpService.request
      .url(`/admin-projects/project`)
      .post({ fortnoxId, name }) // body
      .json<models.ListAdminProjectsResponse>();

    return createdProject;
  }

  async syncProject(projectId: string): Promise<void> {
    await appHttpService.request
      .url(`/admin-projects/project/${projectId}/sync`)
      .post({})
      .res();
  }

  async provisionSharePointSite(
    projectId: string,
  ): Promise<models.ProjectWorkspaceStatus> {
    return await appHttpService.request
      .url(`/admin-projects/project/${projectId}/sharepoint/provision`)
      // A request-scoped catcher pre-empts `appHttpService`'s `catcherFallback`, which opens a
      // generic warning notification for every error. Without this, a bulk sweep over hundreds of
      // projects would spam one toast per throttled project.
      .catcher(HttpStatus.TooManyRequests, (error) => {
        throw httpToClientError(error.json) ?? error;
      })
      .post()
      .json<models.ProjectWorkspaceStatus>();
  }

  /** `null` means the ledger row does not exist (`404`) — not "the request failed". */
  async getSharePointStatus(
    projectId: string,
  ): Promise<models.ProjectWorkspaceStatus | null> {
    const status = await appHttpService.request
      .url(`/admin-projects/project/${projectId}/sharepoint`)
      // Same reason as above, plus: the caller must be able to tell "no ledger row" apart from a
      // transient error, or one blip would reset the row to "not provisioned".
      .catcher(HttpStatus.NotFound, () => null)
      .get()
      .json<models.ProjectWorkspaceStatus | null>();
    return status ?? null;
  }

  async updateProject(
    projectId: string,
    body: models.UpdateProject,
  ): Promise<models.UpdateProject> {
    const adminProject = await appHttpService.request
      .url(`/admin-projects/project/${projectId}`)
      .put(body)
      .json<models.UpdateProject>();
    return adminProject;
  }

  async getAllUsersForProject(
    projectId: string,
  ): Promise<models.ProjectUsersResponse[]> {
    const adminProjects = await appHttpService.request
      .url(`/admin-projects/users/${projectId}`)
      .get()
      .json<models.ProjectUsersResponse[]>();
    return adminProjects;
  }

  async getAvaliableUsersForProject(
    projectId: string,
  ): Promise<models.ProjectUsersResponse[]> {
    const adminProjects = await appHttpService.request
      .url(`/admin-projects/avaliable-users/${projectId}`)
      .get()
      .json<models.ProjectUsersResponse[]>();
    return adminProjects;
  }

  async getPermissionForUserInProject(
    projectId: string,
    userId: string,
  ): Promise<models.Permission> {
    const permission = await appHttpService.request
      .url(`/permission/user-project/${userId}/${projectId}`)
      .get()
      .json<models.Permission>();
    return permission;
  }

  // Request bodies, so these take `WritePermission`: `files` is optional there, which lets a caller
  // that has no files-tier control yet omit it. The responses always carry it, hence `Permission`.
  async addPermission(
    permission: models.WritePermission,
  ): Promise<models.Permission> {
    const addedPermission = await appHttpService.request
      .url('/permission')
      .post(permission)
      .json<models.Permission>();
    return addedPermission;
  }

  async updatePermission(
    permission: models.WritePermission,
  ): Promise<models.Permission> {
    const updatedPermission = await appHttpService.request
      .url(`/permission/${permission.id}`)
      .put(permission)
      .json<models.Permission>();
    return updatedPermission;
  }

  async deletePermissionForUserInProject(
    projectId: string,
    userId: string,
  ): Promise<{ success: boolean }> {
    return await appHttpService.request
      .url(`/permission/user-project/${userId}/${projectId}`)
      .json({})
      .delete()
      .json<{ success: boolean }>();
  }

  async updateUserSettings(
    userId: string,
    body: models.UpdateUserSettingsBody,
  ): Promise<models.UpdateUserSettingsBody> {
    return await appHttpService.request
      .url(`/admin-projects/user-settings/${userId}`)
      .put(body)
      .json<models.UpdateUserSettingsBody>();
  }
}

export const adminProjectsService = new AdminProjectsService();
